import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/adminApi';

interface Stats {
    singlePlayerGames: number;
    multiplayerGames: number;
    totalPlayers: number;
    onlinePlayers: number;
    activePaidSubscriptions: number;
    activeFreeTrials: number;
    playerTrialInvites: number;
}

interface StatsProps {
    onError?: (error: string) => void;
}

const Stats: React.FC<StatsProps> = ({ onError }) => {
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        setLoading(true);
        try {
            const statsResponse = await adminApi.getStats();
            if (statsResponse.status === 'success' && statsResponse.data) {
                setStats(statsResponse.data);
            } else {
                const errorMessage = statsResponse.message || 'Failed to load stats';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to load stats';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className='space-y-6'>
                <h2 className='text-2xl font-bold text-gray-900'>System Statistics</h2>
                <div className='text-center py-8'>
                    <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
                    <p className='mt-4 text-gray-600'>Loading statistics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <h2 className='text-2xl font-bold text-gray-900'>System Statistics</h2>
            {stats ? (
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Total Players</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.totalPlayers}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Online Players</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.onlinePlayers}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Single Player Games</h3>
                        <p className='text-3xl font-bold text-blue-600'>
                            {stats.singlePlayerGames}
                        </p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Multiplayer Games</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.multiplayerGames}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>
                            Active Paid Subscriptions
                        </h3>
                        <p className='text-3xl font-bold text-blue-600'>
                            {stats.activePaidSubscriptions}
                        </p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Active Free Trials</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.activeFreeTrials}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Player Trial Invites</h3>
                        <p className='text-3xl font-bold text-blue-600'>
                            {stats.playerTrialInvites}
                        </p>
                    </div>
                </div>
            ) : (
                <div className='text-center py-8'>
                    <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
                    <p className='mt-4 text-gray-600'>Loading statistics...</p>
                </div>
            )}
        </div>
    );
};

export default Stats;

